import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import MultiLineAvgChart from "../../components/charts3/MultiLineAvgChartInteltube";
import { useYoutubeTracker } from "../../hooks/useYoutubeTracker";
import { useEpgData } from "../../hooks/useEpgData";
import { ChartControls } from "../../components/Channel";
import { Paper } from "@mui/material";
import {
  getTimezoneOffset,
} from "../../utils/timezoneShift";
import { TIMEZONES } from "../../utils/timezones";
import {
  Autocomplete,
  Checkbox,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";

import api from "../../utils/api";
import EpgTimeline from "./EpgTimeline";
import { useUserJourneyLogger } from "../../hooks/useUserJourneyLogger";
const MODE = "minute";
const EPG_CACHE_KEY_PREFIX = "epg_cache_";
const EPG_CACHE_DURATION = 60 * 60 * 1000;








function getStoredChannelIds() {
  try {
    const u = JSON.parse(localStorage.getItem("adm_panel_user") || "{}");

    const arr =
      Array.isArray(u.ytchannels) && u.ytchannels.length > 0
        ? u.ytchannels
        : (u.channels || []);

    return arr
      .map((x) => String(x).trim())
      .filter((x) => x && x !== "null" && x !== "undefined");
  } catch {
    return [];
  }
}

export default function YoutubeTrackerGrid({
  channelId,
  refreshKey,
  refreshEnabled,
  setRefreshEnabled,
  refreshIntervalMin,
  setRefreshIntervalMin,
  refreshCountdown,
  manualRefresh,
}) {
  const [availableChannelIds, setAvailableChannelIds] = useState([]);
  const epgRef = useRef(null);
  const hasScrolledRef = useRef(false);
  const [hasScrolledDown, setHasScrolledDown] = useState(false);


  useEffect(() => {
    setAvailableChannelIds(getStoredChannelIds());
  }, []);

  const [selectedChannelIds, setSelectedChannelIds] = useState([]);
  const [channelInfoById, setChannelInfoById] = useState({});
  const [channelsLoading, setChannelsLoading] = useState(false);

  const [yAxisScale, setYAxisScale] = useState("log");

  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const { logPageChange } = useUserJourneyLogger({ channelId });

  useEffect(() => {
    logPageChange({ chartType: "inteltube" });
  }, [logPageChange]);

const storedUser = JSON.parse(localStorage.getItem("adm_panel_user") || "{}");
const isDark = !!storedUser.darkmode;

const btnStyle = {
  height: "100%",
  px: 2.5,
  textTransform: "none",
  border: "none",
  fontWeight: 500,
  color: isDark ? "#94a3b8" : "#666666",
  display: "flex",
  alignItems: "center",
  "&.Mui-selected": {
    background: isDark ? "#1e293b" : "#ffffff",
    color: isDark ? "#90caf9" : "#1976d2",
    boxShadow: isDark ? "0 2px 6px rgba(0,0,0,0.4)" : "0 2px 6px rgba(0,0,0,0.1)",
  },
  "&.Mui-selected:hover": {
    background: isDark ? "#1e293b" : "#ffffff",
  },
};

const timezoneOptions = useMemo(() => {
  return TIMEZONES.map((tz) => {
    const offset = getTimezoneOffset(tz);
    const sign = offset >= 0 ? "+" : "";

    return {
      value: tz,
      label: `${tz} (UTC${sign}${offset})`,
    };
  });
}, []);

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();

    async function loadChannelInfos() {
      if (!availableChannelIds.length) {
        if (alive) setChannelInfoById({});
        return;
      }

      setChannelsLoading(true);

      try {
        const results = await Promise.all(
          availableChannelIds.map(async (id) => {
            try {
              const res = await api.get(`/ci?i=${encodeURIComponent(id)}`, {
                signal: ac.signal,
              });

              const json = res.data;
              const info = Array.isArray(json) ? json[0] : null;

              return [String(id), info];
            } catch {
              return [String(id), null];
            }
          })
        );

        if (!alive) return;

        const map = {};
        for (const [id, info] of results) {
          if (info && typeof info === "object") map[id] = info;
        }

        setChannelInfoById(map);
      } finally {
        if (alive) setChannelsLoading(false);
      }
    }

    loadChannelInfos();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [availableChannelIds]);

  useEffect(() => {
    if (availableChannelIds.length > 0) {
      setSelectedChannelIds(availableChannelIds);
    }
  }, [availableChannelIds]);

  const channelLabelById = useMemo(() => {
    const labels = {};
    for (const id of availableChannelIds) {
      const info = channelInfoById[id];
      const name = (info?.name || info?.n || "").trim();
      labels[String(id)] = name || String(id);
    }
    return labels;
  }, [availableChannelIds, channelInfoById]);

    const channelColorById = useMemo(() => {
    const colors = {};
    for (const id of availableChannelIds) {
      const info = channelInfoById[id];
      const color = info?.color || info?.Color || "";
      colors[String(id)] = color || null;
    }
    return colors;
  }, [availableChannelIds, channelInfoById]);

  const channelOptions = useMemo(() => {
    return availableChannelIds
      .map((id) => {
        const strId = String(id);
        const label = channelLabelById[strId] || strId;

        return {
          id: strId,
          label,
          search: label.toLowerCase(),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, "tr"));
  }, [availableChannelIds, channelLabelById]);

  const selectedChannelOptions = useMemo(() => {
    const map = new Map(channelOptions.map((o) => [o.id, o]));
    return selectedChannelIds
      .map((id) => map.get(String(id)))
      .filter(Boolean);
  }, [selectedChannelIds, channelOptions]);

  const [highlightChannelId, setHighlightChannelId] = useState(null);

  useEffect(() => {
    if (selectedChannelIds.length < 2) {
      setHighlightChannelId(null);
      return;
    }
    if (
      highlightChannelId != null &&
      !selectedChannelIds.map(String).includes(String(highlightChannelId))
    ) {
      setHighlightChannelId(null);
    }
  }, [selectedChannelIds, highlightChannelId]);

  const today = dayjs().format("YYYY-MM-DD");
  const [startDateInput, setStartDateInput] = useState(today);
  const [endDateInput, setEndDateInput] = useState(today);
  const [startTime, setStartTime] = useState(() => {
    const now = new Date();
    const tzOffset = getTimezoneOffset(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const utcMinute = now.getUTCHours() * 60 + now.getUTCMinutes();
    const currentMinute = ((utcMinute + tzOffset * 60) % 1440 + 1440) % 1440;
    const centeredStart = Math.max(0, Math.round((currentMinute - 3 * 60) / 60) * 60);
    const hh = String(Math.floor(centeredStart / 60)).padStart(2, "0");
    const mm = String(centeredStart % 60).padStart(2, "0");
    return `${hh}:${mm}`;
  });

  const [endTime, setEndTime] = useState(() => {
    const now = new Date();
    const tzOffset = getTimezoneOffset(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const utcMinute = now.getUTCHours() * 60 + now.getUTCMinutes();
    const currentMinute = ((utcMinute + tzOffset * 60) % 1440 + 1440) % 1440;
    const centeredEnd = Math.min(1439, Math.round((currentMinute + 3 * 60) / 60) * 60);
    const hh = String(Math.floor(centeredEnd / 60)).padStart(2, "0");
    const mm = String(centeredEnd % 60).padStart(2, "0");
    return `${hh}:${mm}`;
  });

  const [selectedProgram, setSelectedProgram] = useState(null);


  const [epgByDateAndChannel, setEpgByDateAndChannel] = useState({});
  const [epgLoadingDates, setEpgLoadingDates] = useState(new Set());

  useEffect(() => {
      if (hasScrolledRef.current) return;
      const timer = setTimeout(() => {
        if (!epgRef.current) return;
        epgRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        hasScrolledRef.current = true;
        setHasScrolledDown(true);
      }, 1500);
      return () => clearTimeout(timer);
    }, []);

  useEffect(() => {
      if (!hasScrolledDown) return;
      if (epgLoadingDates.size > 0) return;

      const timer = setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setHasScrolledDown(false);
      }, 3000);

      return () => clearTimeout(timer);
    }, [hasScrolledDown, epgLoadingDates.size]);

  const fetchEpgForDate = useCallback(async (date) => {
    if (!selectedChannelIds.length || !date) return;
    const channelsToFetch = [...selectedChannelIds];
    setEpgLoadingDates((prev) => new Set([...prev, date]));

    const results = await Promise.all(
      channelsToFetch.map(async (id) => {
        const cacheKey = `${EPG_CACHE_KEY_PREFIX}${id}_${date}`;
        try {
          const cached = sessionStorage.getItem(cacheKey);
          if (cached) {
            const { data, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < EPG_CACHE_DURATION) return [id, data];
          }
        } catch {}

        try {
          const res = await api.get(
            `/api/EpgLogs?channel_id=${encodeURIComponent(id)}&key_date=${encodeURIComponent(date)}`
          );
          const raw = Array.isArray(res.data) ? res.data : [];
          const blocks = raw.map((x) => {
            const toDate = (v) => { const d = new Date(v); return isNaN(d) ? null : d; };
            const startDate = toDate(x.start_date);
            const endDate = toDate(x.end_date);
            const startMinute = startDate ? startDate.getUTCHours() * 60 + startDate.getUTCMinutes() : null;
            const endMinute = endDate ? endDate.getUTCHours() * 60 + endDate.getUTCMinutes() : null;
            return {
              id: x.id || x._id || "",
              programName: x.program_name || "",
              startMinute,
              endMinute,
              keyDate: x.key_date || "",
            };
          }).filter((b) => b.id && b.startMinute !== null && b.endMinute !== null);

          try {
            if (blocks.length > 0) {
              sessionStorage.setItem(cacheKey, JSON.stringify({ data: blocks, timestamp: Date.now() }));
            }
          } catch {}

          return [id, blocks];
          } catch {
          // Retry once after 1 second
          try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            const res = await api.get(
              `/api/EpgLogs?channel_id=${encodeURIComponent(id)}&key_date=${encodeURIComponent(date)}`
            );
            const raw = Array.isArray(res.data) ? res.data : [];
            const blocks = raw.map((x) => {
              const toDate = (v) => { const d = new Date(v); return isNaN(d) ? null : d; };
              const startDate = toDate(x.start_date);
              const endDate = toDate(x.end_date);
              const startMinute = startDate ? startDate.getUTCHours() * 60 + startDate.getUTCMinutes() : null;
              const endMinute = endDate ? endDate.getUTCHours() * 60 + endDate.getUTCMinutes() : null;
              return {
                id: x.id || x._id || "",
                programName: x.program_name || "",
                startMinute,
                endMinute,
                keyDate: x.key_date || "",
              };
            }).filter((b) => b.id && b.startMinute !== null && b.endMinute !== null);
          try {
            if (blocks.length > 0) {
              sessionStorage.setItem(cacheKey, JSON.stringify({ data: blocks, timestamp: Date.now() }));
            }
          } catch {}

          return [id, blocks];
          } catch {
            return [id, []];
          }
        }
      })
    );

    const map = {};
    for (const [id, blocks] of results) map[id] = blocks;
    setEpgByDateAndChannel((prev) => ({
      ...prev,
      [date]: map,
    }));
    setEpgLoadingDates((prev) => {
      const next = new Set(prev);
      next.delete(date);
      return next;
    });
}, [selectedChannelIds]);

const handleProgramClick = ({ channelId, shiftedStart, shiftedEnd, viewDate: clickedDate }, callback) => {
  const BASE_OFFSET = 3;
  const targetOffset = getTimezoneOffset(timezone);
  const shiftMinutes = (targetOffset - BASE_OFFSET) * 60;

  const clickedDateFormatted = dayjs(clickedDate).format("DD-MM-YYYY");
  const prevDate = dayjs(clickedDate).subtract(1, "day").format("DD-MM-YYYY");
  const nextDate = dayjs(clickedDate).add(1, "day").format("DD-MM-YYYY");

  const items = (Array.isArray(data) ? data : []).filter(
    (x) => String(x.channelId) === String(channelId)
  );

  if (!items.length) {
    callback(0);
    return;
  }

  const prev = items.find((x) => x.date === prevDate)?.videoConsumption || new Array(1440).fill(0);
  const curr = items.find((x) => x.date === clickedDateFormatted)?.videoConsumption || new Array(1440).fill(0);
  const next = items.find((x) => x.date === nextDate)?.videoConsumption || new Array(1440).fill(0);

  const full = [...prev, ...curr, ...next];
  const baseStart = 1440;
  const shiftedStart2 = baseStart - shiftMinutes;

  let total = 0;
  for (let i = 0; i < 1440; i++) {
    if (i >= shiftedStart && i <= shiftedEnd) {
      let idx = shiftedStart2 + i;
      idx = ((idx % full.length) + full.length) % full.length;
      total += Number(full[idx]) || 0;
    }
  }

  callback(total);
};

  useEffect(() => {
    fetchEpgForDate(startDateInput);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannelIds, startDateInput]);
  const { epgBlocks, loading: epgLoading } = useEpgData(channelId, startDateInput);

  const epgForSelectedDate = useMemo(() => {
    if (!startDateInput) return [];
    return epgBlocks.filter((b) => b.keyDate === startDateInput);
  }, [epgBlocks, startDateInput]);

  const showEpgDropdown =
    Boolean(channelId) && !epgLoading && epgForSelectedDate.length > 0;

  useEffect(() => {
    setSelectedProgram(null);
  }, [startDateInput, channelId]);

  const { data} = useYoutubeTracker(
    selectedChannelIds,
    startDateInput,
    endDateInput,
    refreshKey
  );

  const parseTimeToMinutes = (t) => {
    const [hh, mm] = (t || "").split(":").map(Number);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return 0;
    return hh * 60 + mm;
  };

  const chartXAxisBounds = useMemo(() => {
    const startM = parseTimeToMinutes(startTime);
    const endM = parseTimeToMinutes(endTime);
    const lastMinuteOfDay = 23 * 60 + 59;
    // 1439/60 < 24; Syncfusion + 4h ticks can clip the tail. Snap to 24h for a true end-of-day view.
    const maximumHours = endM >= lastMinuteOfDay ? 24 : endM / 60;
    return {
      minimum: startM / 60,
      maximum: maximumHours,
    };
  }, [startTime, endTime]);

  // 🔥 FIXED TZ LOGIC
  const chartSeries = useMemo(() => {
    const start = parseTimeToMinutes(startTime);
    const end = parseTimeToMinutes(endTime);

    const BASE_OFFSET = 3;
    const targetOffset = getTimezoneOffset(timezone);

    const shiftMinutes = (targetOffset - BASE_OFFSET) * 60;

    const selectedDate = dayjs(startDateInput).format("DD-MM-YYYY");
    const prevDate = dayjs(startDateInput).subtract(1, "day").format("DD-MM-YYYY");
    const nextDate = dayjs(startDateInput).add(1, "day").format("DD-MM-YYYY");
    if (!data || !data.length) {
    return selectedChannelIds.map(id => ({
      name: id,
      channelId: id,
      data: []
    }));
  }

    return selectedChannelIds.map((id) => {
      const items = (Array.isArray(data) ? data : []).filter(
        (x) => String(x.channelId) === String(id)
      );

      if (!items.length)
        return { name: id, channelId: String(id), data: [] };

      const prev = items.find((x) => x.date === prevDate)?.videoConsumption || new Array(1440).fill(0);
      const curr = items.find((x) => x.date === selectedDate)?.videoConsumption || new Array(1440).fill(0);
      const next = items.find((x) => x.date === nextDate)?.videoConsumption || new Array(1440).fill(0);

      const full = [...prev, ...curr, ...next];

      const baseStart = 1440;
      const shiftedStart = baseStart - shiftMinutes;

      const window = [];

      for (let i = 0; i < 1440; i++) {
        let idx = shiftedStart + i;

        // ✅ sağlam wrap
        idx = ((idx % full.length) + full.length) % full.length;

        window.push(full[idx]);
      }

      const finalData = window
        .map((v, i) => ({
          x: i / 60,
          active: Number(v) || 0,
          i,
        }))
        .filter(({ i }) => i >= start && i <= end);

      return {
        name: curr?.channelName || channelLabelById[id] || id,
        channelId: String(id),
        data: finalData,
      };
    });
  }, [
    selectedChannelIds,
    data,
    startTime,
    endTime,
    channelLabelById,
    timezone,
    startDateInput,
  ]);
  const CustomPaper = (props) => {
    return (
      <Paper
        {...props}
        sx={{
          bgcolor: isDark ? "#1a1f2e" : "#ffffff",
          border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.12)",
          backgroundImage: "none",
          "& .MuiAutocomplete-listbox": {
            bgcolor: isDark ? "#1a1f2e" : "#ffffff",
            "& .MuiAutocomplete-option": {
              color: isDark ? "#e0e0e0" : "#111827",
              '&[aria-selected="true"]': {
                bgcolor: isDark ? "rgba(144,202,249,0.15)" : "rgba(25,118,210,0.08)",
              },
              '&.Mui-focused': {
                bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
              },
            },
          },
        }}
      >
        {selectedChannelIds.length >= 2 && (
          <div style={{
            padding: "6px 12px",
            fontSize: 11,
            color: isDark ? "#888" : "#999",
            borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
          }}>
            Left: on chart · Right: spotlight one line
          </div>
        )}
        {props.children}
      </Paper>
    );
  };
  return (
    <>
      <ChartControls
        channelId={channelId}
        mode={MODE}
        hideModeButtons
        startDateInput={startDateInput}
        endDateInput={endDateInput}
        onStartDateChange={(d) => {
          setStartDateInput(d);
          setEndDateInput(d);
        }}
        onEndDateChange={setEndDateInput}
        startTime={startTime}
        endTime={endTime}
        onStartTimeChange={setStartTime}
        onEndTimeChange={setEndTime}
        refreshEnabled={refreshEnabled}
        onRefreshToggle={setRefreshEnabled}
        refreshIntervalMin={refreshIntervalMin}
        onRefreshIntervalChange={setRefreshIntervalMin}
        countdown={refreshCountdown}
        onManualRefresh={manualRefresh}
      />

      <div style={{ display: "grid", gap: 16 }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            padding: 16,
            borderRadius: 16,
            background: "var(--bg-card)",
            border: "1px solid var(--border-color)",
            boxShadow: "0 6px 18px var(--shadow-color)",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: showEpgDropdown
                ? "minmax(220px, 1fr) minmax(220px, 1fr) auto"
                : "minmax(260px, 1fr) auto",
              gap: 16,
              alignItems: "center",
            }}
          >
          <Autocomplete
           PaperComponent={CustomPaper}
            multiple
            disableCloseOnSelect
            options={channelOptions}
            value={selectedChannelOptions}
            loading={channelsLoading}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(_, v) => setSelectedChannelIds(v.map((x) => x.id))}
            renderTags={() => null}
            slotProps={{
              listbox: { sx: { maxHeight: 320 } },
            }}
            renderOption={(props, option, { selected }) => {
              const { key, ...liProps } = props;
              const showSpotlight = selectedChannelIds.length >= 2;
              const isSpotlight =
                highlightChannelId != null &&
                String(highlightChannelId) === String(option.id);

              return (
                <li
                  key={key}
                  {...liProps}
                  style={{
                    ...liProps.style,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <Tooltip title="Show on chart" placement="left">
                    <Checkbox
                      checked={selected}
                      size="small"
                      sx={{ p: 0.35 }}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => {
                        const id = option.id;
                        if (selected) {
                          setSelectedChannelIds((ids) =>
                            ids.filter((x) => String(x) !== String(id))
                          );
                        } else {
                          setSelectedChannelIds((ids) => [...ids, id]);
                        }
                      }}
                    />
                  </Tooltip>
                  {showSpotlight && (
                    <Tooltip
                      title={
                        selected
                          ? "Spotlight: dim other lines (optional)"
                          : "Add to chart first"
                      }
                      placement="left"
                    >
                      <span>
                        <Checkbox
                          size="small"
                          checked={isSpotlight}
                          disabled={!selected}
                          sx={{
                            p: 0.35,
                            "&.Mui-disabled": { opacity: 0.35 },
                            "&.Mui-checked": {
                              color: "primary.main",
                              filter:
                                "drop-shadow(0 0 6px rgba(25, 118, 210, 0.45))",
                            },
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => {
                            if (!selected) return;
                            setHighlightChannelId((cur) =>
                              String(cur) === String(option.id)
                                ? null
                                : String(option.id)
                            );
                          }}
                        />
                      </span>
                    </Tooltip>
                  )}
                  <span style={{ flex: 1, minWidth: 0 }}>{option.label}</span>
                </li>
              );
            }}
            sx={{ width: "100%", minWidth: 200, maxWidth: "100%" }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="YT Channels"
                fullWidth
              />
            )}
          />

          {showEpgDropdown && (
            <TextField
              select
              size="small"
              label="Channel EPG"
              value={selectedProgram?.id || ""}
              onChange={(e) => {
                const prog = epgForSelectedDate.find(
                  (p) => p.id === e.target.value
                );
                setSelectedProgram(prog || null);
                if (prog) {
                  const startH = String(
                    Math.floor(prog.startMinute / 60)
                  ).padStart(2, "0");
                  const startM = String(prog.startMinute % 60).padStart(
                    2,
                    "0"
                  );
                  const endH = String(
                    Math.floor(prog.endMinute / 60)
                  ).padStart(2, "0");
                  const endM = String(prog.endMinute % 60).padStart(2, "0");
                  setStartTime(`${startH}:${startM}`);
                  setEndTime(`${endH}:${endM}`);
                } else {
                  setStartTime("00:00");
                  setEndTime("23:59");
                }
              }}
              sx={{ width: "100%", minWidth: 200 }}
            >
              <MenuItem value="">
                00:00 - 23:59 | Full day
              </MenuItem>
              {epgForSelectedDate.map((prog) => {
                const startH = String(
                  Math.floor(prog.startMinute / 60)
                ).padStart(2, "0");
                const startM = String(prog.startMinute % 60).padStart(2, "0");
                const endH = String(Math.floor(prog.endMinute / 60)).padStart(
                  2,
                  "0"
                );
                const endM = String(prog.endMinute % 60).padStart(2, "0");
                return (
                  <MenuItem key={prog.id} value={prog.id}>
                    {startH}:{startM} - {endH}:{endM} | {prog.programName}
                  </MenuItem>
                );
              })}
            </TextField>
          )}

          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            <ToggleButtonGroup
              value={yAxisScale}
              exclusive
              onChange={(_, val) => val && setYAxisScale(val)}
              sx={{ borderRadius: 2, boxShadow: 0 }}
            >
              <ToggleButton value="linear" sx={btnStyle}>
                Linear
              </ToggleButton>
              <ToggleButton value="log" sx={btnStyle}>
                Logarithmic
              </ToggleButton>
            </ToggleButtonGroup>

              <Autocomplete
                size="small"
                options={timezoneOptions}
                value={timezoneOptions.find((t) => t.value === timezone) || null}
                onChange={(_, val) => val && setTimezone(val.value)}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                sx={{ width: 260, minWidth: 220 }}
                componentsProps={{
                  paper: {
                    sx: {
                      bgcolor: isDark ? "#1a1f2e" : "#ffffff",
                      backgroundImage: "none",
                      border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.12)",
                      "& .MuiAutocomplete-listbox": {
                        bgcolor: isDark ? "#1a1f2e" : "#ffffff",
                        "& .MuiAutocomplete-option": {
                          color: isDark ? "#e0e0e0" : "#111827",
                          '&[aria-selected="true"]': {
                            bgcolor: isDark ? "rgba(144,202,249,0.15)" : "rgba(25,118,210,0.08)",
                          },
                          '&.Mui-focused': {
                            bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
                          },
                        },
                      },
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Timezone" fullWidth />
                )}
              />
          </div>
          </div>
        </div>

      {selectedChannelIds.length > 0 && (
        <MultiLineAvgChart
          title="Inteltube.live"
          refreshKey={refreshKey}
          yAxisScale={yAxisScale}
          series={chartSeries}
          key={`${selectedChannelIds.length}-${data?.length}-${timezone}`}
          xAxisMinimum={chartXAxisBounds.minimum}
          xAxisMaximum={chartXAxisBounds.maximum}
          emphasizedChannelId={highlightChannelId}
          channelColorById={channelColorById}
        />
      )}

      <div ref={epgRef}>
      <EpgTimeline
        epgByDateAndChannel={epgByDateAndChannel}
        isLoading={epgLoadingDates.size > 0}
        selectedChannelIds={selectedChannelIds}
        channelLabelById={channelLabelById}
        channelColorById={channelColorById}
        startMinute={parseTimeToMinutes(startTime)}
        endMinute={parseTimeToMinutes(endTime)}
        timezoneOffsetHours={getTimezoneOffset(timezone)}
        selectedDate={startDateInput}
        onProgramClick={handleProgramClick}
        onDateNeeded={(date) => fetchEpgForDate(date)}
        onDateChange={(date) => {
          setStartDateInput(date);
          setEndDateInput(date);
          manualRefresh();
        }}
        onTimeWindowChange={(start, end) => {
          const toTime = (m) => {
            const clamped = Math.max(0, Math.min(1439, m));
            const hh = String(Math.floor(clamped / 60)).padStart(2, "0");
            const mm = String(clamped % 60).padStart(2, "0");
            return `${hh}:${mm}`;
          };
          setStartTime(toTime(start));
          setEndTime(toTime(Math.min(end, 1439)));
        }}
        isDark={isDark}
        jsx/>
        </div>
      </div>
    </>
  );
}